<template>
  <router-view />
  <text-selection-popup />
</template>

<script>
import { updateDbChild } from 'src/functions/function-fb-crud'
import { handleRouteChange } from 'src/functions/function-route-handler'
import TextSelectionPopup from './components/shared/layout/TextSelectionPopup.vue'
export default {
  components: { TextSelectionPopup },
  name: 'App',
  data() {
    return {
      interval: null,
      pillsToast: null, // update/close handle of the "Loading your pills" notify
    }
  },
  created() {
    if (!import.meta.env.DEV)
      console.log(
        `
      *********
      IMPORTANT
      *********
      Any attempt to hack, exploit, or tamper with this website is strictly prohibited
      and will result in immediate and permanent ban from our platform.
      We also reserve the right to take legal action against any individual or entity
      found to be engaging in such activities.
      `
      )

    // update the time every 5 seconds
    this.interval = setInterval(() => {
      this.$stores.global.refreshTimeNow()
    }, 5000)
  },
  mounted() {
    //if(import.meta.env.DEV) console.time('tdebug')
    //this.handleAuthStateChange()
    this.$stores.auth.handleAuthStateChange()

    this.$stores.global.processUrlParams()

    window.addEventListener('beforeunload', () => this.$stores.auth.detachAuthListeners(), { once: true })
  },
  beforeUnmount() {
    clearInterval(this.interval)
    this.$stores.auth.detachAuthListeners()
  },
  computed: {
    appIsVisible() {
      return this.$q.appVisible
    },
    currentRoute() {
      return this.$router.currentRoute.value.path
    },
    loggedIn() {
      return this.$stores.auth.loggedIn
    },
    getStreak() {
      return this.$stores.progress.progress?.streak
    },
    appVersion() {
      return this.$stores.global.appVersion
    },
    pharmacyVersion() {
      return this.$stores.global.pharmacyVersion
    },
    pillsLoading() {
      return this.$stores.pills.pillsLoading
    },
    currentDate() {
      return this.$stores.global.publicData?.current?.date || ''
    },
    getAllPillIds() {
      return this.$stores.packs.getAllPillIds
    },
    // today's daily pill pack, once the user's data (and their weekly pack prefs) is in
    dailyPillPackToPreload() {
      if (!this.loggedIn || !this.$stores.auth.appReady) return null
      return this.$stores.global.todaysPillPackId || null
    },
  },

  watch: {
    appIsVisible(newValue, oldValue) {
      //this.setAppVisible(newValue)
      this.$stores.auth.updateOnlinePresence({ visible: newValue })
      this.updateLastAccess()
    },
    getStreak() {
      this.updateLastAccess()
    },
    // the date can arrive after the progress: write the access once it is known
    currentDate() {
      this.updateLastAccess()
    },
    currentRoute(newValue, oldValue) {
      this.$stores.auth.updateOnlinePresence()
    },
    // apply the router guard's rule as soon as the auth state changes
    // (e.g. landing page -> /home right after login), not only on the next navigation
    async loggedIn(newValue) {
      // on reload the first route is not resolved yet (still '/'), which would redirect to Home
      await this.$router.isReady()
      const to = handleRouteChange(this.currentRoute, newValue)
      if (to !== this.currentRoute) this.$router.push(to)
    },
    getAllPillIds: {
      handler(newValue) {
        if (!newValue || !newValue.length) return
        // every owned pill not in memory yet (fetchPillsData skips loaded ones), all at once:
        // one store update per chunk (one pill at a time re-rendered the app for every pill)
        this.$stores.pills.fetchPillsData(newValue)
      },
      immediate: true,
    },
    // its pill list, so "Collect your daily pill" opens the checkout at once
    dailyPillPackToPreload: {
      handler(packId) {
        if (packId) this.$stores.packs.preloadPacksData([packId])
      },
      immediate: true,
    },
    // first download of many pills (new device, cleared cache): a bottom toast with the count, closed when done
    pillsLoading: {
      deep: true,
      handler(loading) {
        if (!loading) {
          this.pillsToast?.()
          this.pillsToast = null
          return
        }
        const caption = `${loading.done} / ${loading.total}`
        if (this.pillsToast) this.pillsToast({ caption })
        else if (loading.total > 20) {
          this.pillsToast = this.$q.notify({
            message: 'Loading your pills',
            caption,
            spinner: true,
            timeout: 0,
            group: false,
            position: 'bottom',
          })
        }
      },
    },
    // a pack was published while the app is open: read the public packs again (the first value, null -> loaded, is skipped)
    pharmacyVersion(newValue, oldValue) {
      if (oldValue !== null && newValue > oldValue) this.$stores.packs.fetchPublicPacksInfos(true)
    },
    appVersion(newValue, oldValue) {
      // a new deploy while this tab is open: ask to refresh. The first value (null -> loaded version) is skipped.
      // Not in the Android app: a reload there runs the same bundled code.
      if (oldValue !== null && newValue > oldValue && !this.$q.platform.is.capacitor) {
        this.$q
          .dialog({
            title: 'New Version Available',
            message: 'A new version is available. Please refresh to get the latest updates.',
            persistent: true,
            ok: { label: 'Refresh' },
          })
          .onOk(() => {
            this.$router.go(0)
          })
      }
    },
  },
  methods: {
    //...mapActions('auth', ['handleAuthStateChange']),
    updateLastAccess() {
      if (!this.$stores.user.userAccess) return
      if (!this.appIsVisible) return
      // never write an empty date: the setDailyStreak function would then reset the streak on the next write
      if (!this.currentDate) return
      if (this.getStreak?.lastAccess === this.currentDate) return

      updateDbChild({
        path: `user-progress/${this.$stores.user.uid}/streak/`,
        data: { lastAccess: this.currentDate },
      })
    },
  },
}
</script>
